import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Video,
  Image as ImageIcon,
  Captions,
  Scissors,
  AudioLines,
  Layers3,
  ArrowRight,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { apiService } from '@/services/api';

const tools = [
  {
    id: 'reels',
    title: 'Criar Reel',
    description: 'Transforme um vídeo em formato vertical 9:16 pronto para redes sociais.',
    icon: Video,
    status: 'Disponível',
  },
  {
    id: 'video',
    title: 'Editar vídeo',
    description: 'Corte, ajuste o formato e prepare versões para diferentes canais.',
    icon: Scissors,
    status: 'Em breve',
  },
  {
    id: 'image',
    title: 'Editar imagem',
    description: 'Redimensione e personalize imagens mantendo a identidade visual.',
    icon: ImageIcon,
    status: 'Em breve',
  },
  {
    id: 'captions',
    title: 'Criar legendas',
    description: 'Transcreva vídeos e gere legendas automaticamente.',
    icon: Captions,
    status: 'Em breve',
  },
  {
    id: 'audio',
    title: 'Tratar áudio',
    description: 'Prepare áudio e voz para vídeos, aulas e conteúdos digitais.',
    icon: AudioLines,
    status: 'Em breve',
  },
  {
    id: 'collections',
    title: 'Coleções terapêuticas',
    description: 'Organize os seus conteúdos por temas e finalidades de uso.',
    icon: Layers3,
    status: 'Em breve',
  },
];

const StudioIntegrativo = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState('');
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadStudio = async () => {
      try {
        setLoading(true);
        const [capabilityPayload, mediaPayload] = await Promise.all([
          apiService.getStudioCapabilities(),
          apiService.getMedia({ type: 'video', limit: 100 }),
        ]);

        if (!mounted) return;
        setReady(Boolean(capabilityPayload?.engines?.ffmpeg?.available));
        setVideos(mediaPayload?.media || []);
      } catch (error) {
        if (!mounted) return;
        setMessage({
          type: 'error',
          text: error.message || 'Não foi possível carregar o Studio.',
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadStudio();
    return () => {
      mounted = false;
    };
  }, []);

  const selected = useMemo(
    () => videos.find((video) => String(video.id) === String(selectedVideo)),
    [videos, selectedVideo]
  );

  const handleCreateReel = async () => {
    if (!selectedVideo || processing) return;

    try {
      setProcessing(true);
      setMessage(null);
      const payload = await apiService.createStudioReel(Number(selectedVideo));
      setMessage({
        type: 'success',
        text: payload?.message || 'Seu Reel foi criado com sucesso.',
        mediaId: payload?.media?.id,
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Não foi possível criar o Reel.',
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-8 pb-10">
      <motion.section
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-500 p-8 md:p-10 shadow-2xl shadow-emerald-500/10"
      >
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
            <Sparkles className="h-3.5 w-3.5" />
            Studio Integrativo
          </div>

          <h1 className="mt-5 text-3xl font-black tracking-tight text-white md:text-5xl">
            Transforme conteúdo em poucos passos.
          </h1>

          <p className="mt-4 max-w-2xl text-base leading-relaxed text-emerald-50 md:text-lg">
            Use vídeos, imagens e outros ativos do MediaHub para criar conteúdos terapêuticos prontos para utilização.
          </p>
        </div>

        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
      </motion.section>

      <section className="rounded-2xl border border-emerald-500/20 bg-slate-900/60 p-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-400">
              <Video className="h-4 w-4" />
              Reels Terapêuticos
            </div>
            <h2 className="mt-2 text-2xl font-bold text-slate-100">Criar Reel 9:16</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              Escolha um vídeo da sua biblioteca. O original é preservado e uma nova versão vertical será criada.
            </p>
          </div>

          <div className="w-full max-w-xl space-y-3">
            {loading ? (
              <div className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-4 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando seus vídeos...
              </div>
            ) : (
              <>
                <select
                  value={selectedVideo}
                  onChange={(event) => setSelectedVideo(event.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm text-slate-200 outline-none focus:border-cyan-500"
                >
                  <option value="">Escolha um vídeo</option>
                  {videos.map((video) => (
                    <option key={video.id} value={video.id}>
                      {video.original_name || video.filename}
                    </option>
                  ))}
                </select>

                {videos.length === 0 && (
                  <p className="text-xs text-slate-500">
                    Adicione um vídeo ao MediaHub para começar.
                  </p>
                )}
              </>
            )}

            {selected && (
              <p className="text-xs text-slate-500">
                Selecionado: {selected.original_name || selected.filename}
              </p>
            )}

            <button
              type="button"
              onClick={handleCreateReel}
              disabled={!selectedVideo || !ready || processing}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 px-5 py-3 text-sm font-bold text-slate-950 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Criando seu Reel...
                </>
              ) : (
                <>
                  Criar Reel
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            {message && (
              <div
                className={`rounded-xl border px-4 py-3 text-sm ${
                  message.type === 'success'
                    ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300'
                    : 'border-red-500/20 bg-red-500/10 text-red-300'
                }`}
              >
                <div>{message.text}</div>
                {message.mediaId && (
                  <button
                    type="button"
                    onClick={() => navigate(`/media/item/${message.mediaId}`)}
                    className="mt-2 font-semibold underline underline-offset-4"
                  >
                    Ver resultado
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section>
        <div className="mb-5">
          <h2 className="text-2xl font-bold text-slate-100">Ferramentas do Studio</h2>
          <p className="mt-1 text-sm text-slate-500">
            Novas ferramentas serão liberadas à medida que forem ficando prontas para uso.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tools.map((tool, index) => (
            <motion.article
              key={tool.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="rounded-2xl border border-slate-800 bg-slate-900/55 p-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-300">
                  <tool.icon className="h-5 w-5" />
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                  tool.status === 'Disponível'
                    ? 'bg-emerald-500/10 text-emerald-400'
                    : 'bg-slate-800 text-slate-500'
                }`}>
                  {tool.status}
                </span>
              </div>

              <h3 className="mt-4 text-lg font-bold text-slate-100">{tool.title}</h3>
              <p className="mt-2 min-h-12 text-sm leading-relaxed text-slate-400">{tool.description}</p>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default StudioIntegrativo;
